import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, SealCheck, Play, LockKey, Prohibit, FilePdf } from "@phosphor-icons/react";
import PermitDialog from "./PermitDialog";
import PdfBrandingDialog, { downloadBlob } from "./PdfBrandingDialog";
import { fmtDateTime } from "../../lib/dateUtil";

export const PERMIT_STATUS_CLASS = {
  draft: "bg-slate-100 text-slate-700", issued: "bg-blue-100 text-blue-700", active: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-200 text-slate-600", cancelled: "bg-red-100 text-red-700",
};
const FILTERS = ["", "draft", "issued", "active", "closed", "cancelled"];

/** Permits to work register with lifecycle actions: issue → (operative confirms) active → close / cancel. */
export default function PermitsPanel({ entityType, entityId, embedded = false, library }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [dlg, setDlg] = useState({ open: false, permit: null });
  const [pdfFor, setPdfFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const permitTypes = library?.permit_types || [];

  const downloadPdf = async (branding) => {
    const p = pdfFor;
    const resp = branding ? await API.post(`/hs/permits/${p.id}/pdf`, branding, { responseType: "blob" }) : await API.get(`/hs/permits/${p.id}/pdf`, { responseType: "blob" });
    downloadBlob(resp.data, `${p.reference}.pdf`);
    toast.success(`${p.reference} PDF downloaded`);
  };

  const load = useCallback(() => {
    setLoading(true);
    API.get("/hs/permits", { params: { status: status || undefined, entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => setRows(data.permits || [])).catch(() => {}).finally(() => setLoading(false));
  }, [status, entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  const act = async (p, action) => {
    let body = {};
    if (action === "close" || action === "cancel") {
      const notes = window.prompt(action === "close" ? "Closure notes (work complete, area made safe, isolations removed…)" : "Reason for cancelling");
      if (notes === null) return;
      body = { closure_notes: notes };
    }
    try { await API.post(`/hs/permits/${p.id}/${action}`, body); toast.success(`${p.reference} ${action === "issue" ? "issued" : action + "d"}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete draft ${p.reference}?`)) return;
    try { await API.delete(`/hs/permits/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const openNew = () => setDlg({ open: true, permit: entityType && entityId ? { entity_type: entityType, entity_id: entityId } : null });

  return (
    <div className="space-y-3" data-testid="permits-panel">
      <div className="flex flex-wrap items-center gap-2">
        {!embedded && (
          <div className="flex gap-1 flex-wrap">
            {FILTERS.map(s => (
              <button key={s} onClick={() => setStatus(s)} className={`text-xs px-2.5 py-1 rounded-full border ${status === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`permits-filter-${s || "all"}`}>{s ? s[0].toUpperCase() + s.slice(1) : "All"}</button>
            ))}
          </div>
        )}
        <Button size="sm" className="ml-auto" onClick={openNew} data-testid="permit-new-btn"><Plus size={14} className="mr-1" /> New permit</Button>
      </div>

      {loading && rows.length === 0 ? <p className="text-xs text-muted-foreground p-4">Loading…</p> : rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground" data-testid="permits-empty">No permits to work{status ? ` with status "${status}"` : ""}. Raise one for hot work, working at height, isolations, confined spaces and more.</div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-[10px] uppercase text-muted-foreground"><tr><th className="text-left p-2">Permit</th><th className="text-left p-2">Type / location</th><th className="text-left p-2 hidden md:table-cell">Issued to</th><th className="text-left p-2 hidden lg:table-cell">Valid</th><th className="text-left p-2">Status</th><th className="p-2"></th></tr></thead>
            <tbody>
              {rows.map(p => {
                const done = (p.precautions || []).filter(x => x.checked).length;
                return (
                  <tr key={p.id} className="border-t border-border hover:bg-muted/40" data-testid={`permit-row-${p.id}`}>
                    <td className="p-2 whitespace-nowrap"><span className="font-semibold text-primary">{p.reference}</span><br /><span className="text-muted-foreground">{p.title}</span></td>
                    <td className="p-2">{p.type_label}{p.location && <span className="text-muted-foreground"> · {p.location}</span>}{p.entity_id && <><br /><Link to={`/${p.entity_type === "emergency_job" ? "emergency-jobs" : "jobs"}/${p.entity_id}`} className="text-primary hover:underline">{p.entity_title || "Linked job"}</Link></>}</td>
                    <td className="p-2 hidden md:table-cell text-muted-foreground">{(p.issued_to || []).map(o => o.name).join(", ") || "—"}</td>
                    <td className="p-2 hidden lg:table-cell text-muted-foreground whitespace-nowrap">{p.valid_from ? `${fmtDateTime(p.valid_from)} → ${fmtDateTime(p.valid_to)}` : "—"}</td>
                    <td className="p-2"><Badge className={`text-[10px] ${PERMIT_STATUS_CLASS[p.status]}`}>{p.status}</Badge>{["issued", "active"].includes(p.status) && <span className="ml-1 text-[10px] text-muted-foreground">{done}/{(p.precautions || []).length} precautions</span>}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {p.status === "draft" && <Button variant="ghost" size="sm" className="h-7 px-2 text-emerald-700" title="Issue" onClick={() => act(p, "issue")} data-testid={`permit-issue-${p.id}`}><SealCheck size={14} className="mr-1" /> Issue</Button>}
                      {p.status === "issued" && <Button variant="ghost" size="sm" className="h-7 px-2" title="Activate (work may start)" onClick={() => act(p, "activate")} data-testid={`permit-activate-${p.id}`}><Play size={14} className="mr-1" /> Activate</Button>}
                      {["issued", "active"].includes(p.status) && <Button variant="ghost" size="sm" className="h-7 px-2" title="Close permit" onClick={() => act(p, "close")} data-testid={`permit-close-${p.id}`}><LockKey size={14} className="mr-1" /> Close</Button>}
                      {!["closed", "cancelled"].includes(p.status) && p.status !== "draft" && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" title="Cancel" onClick={() => act(p, "cancel")} data-testid={`permit-cancel-${p.id}`}><Prohibit size={14} /></Button>}
                      {!["closed", "cancelled"].includes(p.status) && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Edit" onClick={() => setDlg({ open: true, permit: p })} data-testid={`permit-edit-${p.id}`}><PencilSimple size={14} /></Button>}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download branded PDF" onClick={() => setPdfFor(p)} data-testid={`permit-pdf-${p.id}`}><FilePdf size={14} /></Button>
                      {p.status === "draft" && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600" title="Delete" onClick={() => remove(p)} data-testid={`permit-delete-${p.id}`}><Trash size={14} /></Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <PermitDialog open={dlg.open} onOpenChange={(o) => setDlg(d => ({ ...d, open: o }))} permit={dlg.permit} permitTypes={permitTypes} onSaved={load} />
      <PdfBrandingDialog open={!!pdfFor} onOpenChange={(o) => { if (!o) setPdfFor(null); }} clientId={pdfFor?.client_id} onDownload={downloadPdf} title="Permit PDF branding"
                         description="The permit prints with the linked client's white-label by default — override the company name, logo or colours for this download." />
    </div>
  );
}
